/* eslint-disable */
import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/th';

import PinSetup from 'src/sections/login/pin-setup';

const API = 'https://api-hr.boonsiri.co.th';

function digits6(value) {
  return String(value || '').replace(/\D/g, '').slice(0, 6);
}

export default function ChangePinDialog({ open, sessionToken, required = false, onClose }) {
  const [mode, setMode] = useState('loading');
  const [currentPin, setCurrentPin] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [employeeCode, setEmployeeCode] = useState('');
  const [birthDate, setBirthDate] = useState(null);
  const [idLast4, setIdLast4] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setMode('loading');
      setCurrentPin('');
      setPin('');
      setConfirmPin('');
      setEmployeeCode('');
      setBirthDate(null);
      setIdLast4('');
      setError('');
      setLoading(false);
      return undefined;
    }
    if (!sessionToken) {
      setMode('setup');
      setError('กรุณาเข้าสู่ระบบใหม่');
      return undefined;
    }
    if (required) {
      setMode('setup');
      return undefined;
    }
    let cancelled = false;
    fetch(`${API}/auth/password-status`, {
      headers: { Authorization: `Bearer ${sessionToken}` },
    })
      .then((res) => res.json())
      .then((result) => {
        if (cancelled) return;
        if (typeof result?.hasPin !== 'boolean') {
          setError('ตรวจสอบ PIN ไม่ได้');
          setMode('unknown');
          return;
        }
        setMode(result.hasPin ? 'change' : 'setup');
      })
      .catch(() => {
        if (cancelled) return;
        setError('ตรวจสอบ PIN ไม่ได้');
        setMode('unknown');
      });
    return () => {
      cancelled = true;
    };
  }, [open, required, sessionToken]);

  const save = async (event) => {
    if (event) event.preventDefault();
    if (pin.length !== 6 || pin !== confirmPin) {
      setError('PIN ต้องเป็นตัวเลข 6 หลักและตรงกันทั้งสองช่อง');
      return;
    }
    if (mode === 'change' && currentPin.length !== 6) {
      setError('กรอก PIN เดิมก่อนเปลี่ยน');
      return;
    }
    if (mode === 'forgot' && (!employeeCode.trim() || !birthDate || idLast4.length !== 4)) {
      setError('กรอกรหัสพนักงาน วันเกิด และเลขท้ายบัตร 4 หลัก');
      return;
    }
    if (!sessionToken) {
      setError('กรุณาเข้าสู่ระบบใหม่');
      return;
    }
    setLoading(true);
    setError('');
    const body = { pin, confirmPin };
    if (mode === 'change') body.currentPin = currentPin;
    if (mode === 'forgot') {
      body.forgot = true;
      body.employeeCode = employeeCode.trim();
      body.birthDate = dayjs(birthDate).format('YYYY-MM-DD');
      body.idLast4 = idLast4;
    }
    try {
      const res = await fetch(`${API}/setLoginPin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify(body),
      });
      const result = await res.json().catch(() => ({}));
      if (result.status === 'success') {
        setMode('done');
        return;
      }
      setError(result.message || 'ตั้ง PIN ไม่สำเร็จ');
    } catch (err) {
      console.error(err);
      setError('ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
    } finally {
      setLoading(false);
    }
  };

  const forced = required || mode === 'setup';
  const close = forced && mode !== 'done' ? undefined : onClose;

  return (
    <Dialog open={open} fullWidth maxWidth="xs" onClose={loading ? undefined : close}>
      <DialogContent>
        {mode === 'loading' ? (
          <Typography variant="body2">กำลังตรวจสอบ PIN</Typography>
        ) : mode === 'unknown' ? (
          <>
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {error || 'ตรวจสอบ PIN ไม่ได้'}
            </Typography>
            <Button fullWidth variant="contained" onClick={onClose}>
              ปิด
            </Button>
          </>
        ) : mode === 'done' ? (
          <>
            <Typography variant="h6">ตั้ง PIN แล้ว</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              ใช้ PIN นี้เข้าสู่ระบบแทนรหัสผ่านได้
            </Typography>
            <Button fullWidth variant="contained" onClick={onClose}>
              ปิด
            </Button>
          </>
        ) : (
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
            <Box component="form" onSubmit={save}>
              {mode === 'change' ? (
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                  <TextField
                    autoFocus
                    label="PIN เดิม"
                    value={currentPin}
                    onChange={(e) => setCurrentPin(digits6(e.target.value))}
                    inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'off' }}
                  />
                  <Button type="button" onClick={() => setMode('forgot')} sx={{ alignSelf: 'flex-start', px: 0 }}>
                    ลืม PIN
                  </Button>
                </Stack>
              ) : null}
              {mode === 'forgot' ? (
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    ยืนยันรหัสพนักงาน วันเกิด และเลขท้ายบัตรประชาชน 4 หลัก แล้วตั้ง PIN ใหม่
                  </Typography>
                  <TextField
                    label="รหัสพนักงาน"
                    value={employeeCode}
                    onChange={(e) => setEmployeeCode(e.target.value)}
                  />
                  <DatePicker
                    label="วันเกิด"
                    value={birthDate}
                    format="DD/MM/YYYY"
                    onChange={setBirthDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  <TextField
                    label="เลขท้ายบัตร 4 หลัก"
                    value={idLast4}
                    onChange={(e) => setIdLast4(String(e.target.value || '').replace(/\D/g, '').slice(0, 4))}
                    inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                  />
                  <Button type="button" onClick={() => setMode('change')} sx={{ alignSelf: 'flex-start', px: 0 }}>
                    กลับไปกรอก PIN เดิม
                  </Button>
                </Stack>
              ) : null}
              <PinSetup
                pin={pin}
                confirmPin={confirmPin}
                onPin={setPin}
                onConfirm={setConfirmPin}
                onSubmit={save}
                onSkip={onClose}
                loading={loading}
                hideSkip={mode === 'setup'}
                lead={
                  mode === 'setup'
                    ? 'ยังไม่ได้ตั้ง PIN ต้องตั้งก่อนใช้งานต่อ ห้ามซ้ำของเดิม ห้ามเลขเรียง และห้ามใช้วันเดือนปีเกิด'
                    : 'PIN ใหม่ห้ามซ้ำของเดิม ห้ามเลขเรียง และห้ามใช้วันเดือนปีเกิด'
                }
              />
              {error ? (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {error}
                </Typography>
              ) : null}
            </Box>
          </LocalizationProvider>
        )}
      </DialogContent>
    </Dialog>
  );
}
